import { useRef, useEffect, useCallback } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { TOUR_STORAGE_KEY } from '../utils/reportHelpers';

const useManagerReportTour = ({ reportData, activeComponent }) => {
    const summaryTourRef = useRef(null);

    const startSummaryTour = useCallback(() => {
        if (summaryTourRef.current) {
            summaryTourRef.current.complete();
        }
        const tour = new Shepherd.Tour({
            useModalOverlay: true,
            defaultStepOptions: {
                cancelIcon: { enabled: true },
                scrollTo: { behavior: 'smooth', block: 'center' },
                classes: 'shepherd-theme-custom'
            }
        });

        tour.addStep({
            id: 'summary-tab-intro',
            attachTo: { element: '.table-tab:nth-child(2)', on: 'bottom' },
            title: 'Costing Summary',
            text: 'View your costing summary here \u2014 see costs broken down by period, week, site, and department with interactive charts and drill-down tables',
            buttons: [{ text: 'Next', action: tour.next }],
            when: {
                show() {
                    if (!document.querySelector('.table-tab:nth-child(2)')) {
                        tour.next();
                    }
                }
            }
        });

        tour.addStep({
            id: 'book-entertainment-intro',
            attachTo: { element: '.book-entertainment-btn', on: 'bottom' },
            title: 'Book Entertainment',
            text: 'Click here to book live entertainment from over 20k acts',
            buttons: [{ text: 'Next', action: tour.next }],
            when: {
                show() {
                    if (!document.querySelector('.book-entertainment-btn')) {
                        tour.next();
                    }
                }
            }
        });

        tour.addStep({
            id: 'manage-security-intro',
            attachTo: { element: '.manage-security-nav', on: 'bottom' },
            title: 'Requirements',
            text: 'Click here to manage and book security requirements',
            buttons: [{ text: 'Got it', action: tour.complete }],
            when: {
                show() {
                    if (!document.querySelector('.manage-security-nav')) {
                        tour.complete();
                    }
                }
            }
        });

        tour.on('complete', () => {
            localStorage.setItem(TOUR_STORAGE_KEY, 'true');
        });
        tour.on('cancel', () => {
            localStorage.setItem(TOUR_STORAGE_KEY, 'true');
        });
        summaryTourRef.current = tour;
        tour.start();
    }, []);

    // Auto-start tour when data is ready and tab element is present
    useEffect(() => {
        if (!reportData || reportData.length === 0) return;
        if (localStorage.getItem(TOUR_STORAGE_KEY)) return;
        if (activeComponent !== 'costing') return;

        const interval = setInterval(() => {
            const tab = document.querySelector('.table-tab:nth-child(2)');
            if (tab) {
                clearInterval(interval);
                startSummaryTour();
            }
        }, 300);
        return () => clearInterval(interval);
    }, [reportData, activeComponent, startSummaryTour]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (summaryTourRef.current) {
                summaryTourRef.current.off('complete');
                summaryTourRef.current.off('cancel');
                summaryTourRef.current.complete();
            }
        };
    }, []);

    return { startSummaryTour };
};

export default useManagerReportTour;
